import { DomainError } from "./errors";
import { toSafe } from "./arith";
import { MAX_STOCK_QTY_Q4 } from "./stock";
import type { BaseState, TransitionAction } from "./status-flow";

// ===== Ordens de produção e estrutura (RF-SUP-05, T3.2) =====

/** Insumos por estrutura (e por ordem de produção). */
export const MAX_STRUCTURE_ITEMS = 200;
/** Perda máxima: 99,9999% (pct4). Com 100% o necessário dobraria sem limite útil. */
export const MAX_LOSS_PCT4 = 999_999;
/** Apontamentos ativos por ordem (cada um é uma transação com até 201 movimentos). */
export const MAX_ACTIVE_RUNS = 100;

const isInt = Number.isSafeInteger;

/** Divisão inteira com arredondamento comercial (meio para cima). `num` ≥ 0, `den` > 0. */
const roundHalfUp = (num: bigint, den: bigint): bigint => (2n * num + den) / (2n * den);

function assertQty(q: number, what = "Quantidade") {
  if (!isInt(q) || q <= 0 || q > MAX_STOCK_QTY_Q4)
    throw new DomainError("invalid_qty", `${what} inválida: ${q}`);
}

function assertLoss(p: number) {
  if (!isInt(p) || p < 0 || p > MAX_LOSS_PCT4)
    throw new DomainError("invalid_pct", "A perda deve ficar entre 0% e 99,9999%.");
}

/**
 * Necessário de um insumo para produzir `qtyQ4` do acabado: qtd × quantidade por unidade × (1 + perda), em q4 com
 * arredondamento comercial. Zero (insumo pequeno demais para a quantidade) é recusado, salvo `allowZero` (apontamento
 * parcial: o último apontamento completa o previsto).
 */
export function componentRequirementQ4(
  qtyPerUnitQ4: number,
  lossPct4: number,
  qtyQ4: number,
  opts: { allowZero?: boolean } = {},
): number {
  assertQty(qtyPerUnitQ4, "Quantidade do insumo");
  assertLoss(lossPct4);
  assertQty(qtyQ4);
  const n = roundHalfUp(
    BigInt(qtyQ4) * BigInt(qtyPerUnitQ4) * BigInt(1_000_000 + lossPct4),
    10_000n * 1_000_000n,
  );
  if (n > BigInt(MAX_STOCK_QTY_Q4))
    throw new DomainError("invalid_qty", "Quantidade do insumo acima do limite para esta produção.");
  if (n === 0n && !opts.allowZero)
    throw new DomainError(
      "invalid_qty",
      "Quantidade pequena demais: um insumo ficaria com quantidade zero. Aumente a quantidade a produzir.",
    );
  return Number(n);
}

export type StructureItem = { componentId: string; qtyQ4: number; lossPct4: number };

/** Problema da estrutura (lista de insumos) ou null: teto, o próprio produto, repetido, quantidade e perda. */
export function structureProblem(productId: string, items: readonly StructureItem[]): string | null {
  if (items.length > MAX_STRUCTURE_ITEMS) return `A estrutura aceita até ${MAX_STRUCTURE_ITEMS} insumos.`;
  const seen = new Set<string>();
  for (const it of items) {
    if (it.componentId === productId) return "O produto não pode ser insumo do próprio produto.";
    if (seen.has(it.componentId)) return "Insumo repetido na estrutura.";
    seen.add(it.componentId);
    if (!isInt(it.qtyQ4) || it.qtyQ4 <= 0 || it.qtyQ4 > MAX_STOCK_QTY_Q4)
      return "Informe a quantidade de cada insumo.";
    if (!isInt(it.lossPct4) || it.lossPct4 < 0 || it.lossPct4 > MAX_LOSS_PCT4)
      return "A perda deve ficar entre 0% e 99,9999%.";
  }
  return null;
}

/**
 * Ciclo: algum insumo (ou insumo de insumo, pela estrutura gravada) usa `productId`. `children` devolve os insumos da
 * estrutura de um produto. Busca em profundidade com visitados (estrutura antiga com laço não trava).
 */
export function structureCycle(
  productId: string,
  componentIds: readonly string[],
  children: (id: string) => readonly string[],
): boolean {
  const seen = new Set<string>();
  const stack = [...componentIds];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === productId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    stack.push(...children(id));
  }
  return false;
}

export type ProductionBalance = { plannedQ4: number; producedQ4: number; closedQ4: number };
export type ProductionItemBalance = {
  id: string;
  qtyPerUnitQ4: number;
  lossPct4: number;
  requiredQ4: number;
  consumedQ4: number;
};

/** O que falta produzir: planejado − produzido − encerrado (nunca negativo). */
export const productionRemainingQ4 = (o: ProductionBalance): number =>
  Math.max(0, o.plannedQ4 - o.producedQ4 - o.closedQ4);

export type ProductionProgress = "none" | "partial" | "complete";

export function productionProgress(o: ProductionBalance): ProductionProgress {
  if (productionRemainingQ4(o) === 0) return "complete";
  return o.producedQ4 > 0 ? "partial" : "none";
}

/**
 * Consumo de um apontamento de `runQ4` (05 §5 + RF-SUP-05): proporcional à quantidade (necessário com perda, zero fica
 * de fora); o apontamento que completa o planejado consome o que falta do previsto de cada insumo, então a soma dos
 * apontamentos nunca fica abaixo do previsto. Acima do saldo = `production_qty_exceeded`.
 */
export function runConsumption(
  items: readonly ProductionItemBalance[],
  order: ProductionBalance,
  runQ4: number,
): { itemId: string; qtyQ4: number }[] {
  assertQty(runQ4);
  const remaining = productionRemainingQ4(order);
  if (runQ4 > remaining)
    throw new DomainError(
      "production_qty_exceeded",
      "A quantidade passa do saldo a produzir desta ordem de produção.",
    );
  const completes = order.producedQ4 + runQ4 >= order.plannedQ4;
  const out: { itemId: string; qtyQ4: number }[] = [];
  for (const it of items) {
    const qtyQ4 = completes
      ? Math.max(0, it.requiredQ4 - it.consumedQ4)
      : componentRequirementQ4(it.qtyPerUnitQ4, it.lossPct4, runQ4, { allowZero: true });
    if (qtyQ4 > 0) out.push({ itemId: it.id, qtyQ4 });
  }
  return out;
}

/** Custo dos insumos consumidos: Σ quantidade (q4) × custo unitário, em BigInt com arredondamento comercial no fim. */
export function productionCostCents(lines: readonly { qtyQ4: number; unitCostCents: number }[]): number {
  let total = 0n;
  for (const l of lines) total += BigInt(l.qtyQ4) * BigInt(l.unitCostCents);
  return toSafe(roundHalfUp(total, 10_000n), "invalid_money");
}

/** Custo unitário do acabado = custo dos insumos ÷ quantidade produzida (centavos por unidade, meio para cima). */
export function producedUnitCostCents(totalCostCents: number, qtyQ4: number): number {
  if (qtyQ4 <= 0 || totalCostCents <= 0) return 0;
  return toSafe(roundHalfUp(BigInt(totalCostCents) * 10_000n, BigInt(qtyQ4)), "invalid_money");
}

/** Trava da edição da OP: finalizada, cancelada ou com apontamento (mesmo estornado). */
export function productionLockProblem(base: BaseState, runCount: number): string | null {
  if (base === "done") return "Ordem de produção finalizada não se edita.";
  if (base === "canceled") return "Ordem de produção cancelada não se edita.";
  if (runCount > 0) return "Ordem de produção com apontamento não se edita (os insumos já foram consumidos).";
  return null;
}

export type ProductionTransitionPlan = { produceRemaining: boolean; reverseAll: boolean; reopen: boolean };

/**
 * Ações da transição da OP (como a compra, T2.6): lançar estoque = produzir o saldo; estornar estoque ou destino
 * cancelado = estornar os apontamentos ativos; sair de finalizada/cancelada para aberta/em andamento reabre o saldo
 * encerrado.
 */
export function productionTransitionPlan(
  actions: readonly TransitionAction[],
  targetBase: BaseState,
  fromBase: BaseState,
): ProductionTransitionPlan {
  return {
    produceRemaining: targetBase !== "canceled" && actions.includes("post_stock"),
    reverseAll: targetBase === "canceled" || actions.includes("reverse_stock"),
    reopen:
      (fromBase === "done" || fromBase === "canceled") &&
      (targetBase === "open" || targetBase === "in_progress"),
  };
}
